import { FunctionComponent, useMemo } from 'react'

import styled, { css } from 'styled-components'

import translate from '@i18n'
import useRecentMatchOutcomes from '@hooks/useRecentMatchOutcomes'
import {
  SubsectionTitle,
  SubsectionWrapper,
} from '@components/LayoutTabs/SubsectionReusableComponents'
import Placeholder from '@components/LayoutTabs/SubsectionReusableComponents/Placeholder'
import { SvgIconName } from '@components/SvgIcon/types'

import TeamOutcomes from './TeamOutcomes'

export interface RecentMatchOutcomesProps {
  title: string
  typeOfSport: 'soccer'
  matchId?: string
  competitorId?: string
}

const StyledContentWrapper = styled.div`
  ${({
    theme: {
      spacing: { spacing2, spacing8, spacing64 },
    },
  }) => css`
    display: flex;
    gap: ${spacing8};
    min-height: calc(${spacing64} + ${spacing2});
  `}
`

const RecentMatchOutcomes: FunctionComponent<RecentMatchOutcomesProps> = ({
  title,
  matchId,
  typeOfSport,
  competitorId,
}) => {
  const {
    data: recentMatchOutcomesData,
    error,
    isFetched,
  } = useRecentMatchOutcomes({
    typeOfSport,
    matchId,
    competitorId,
  })

  const isCompetitorData =
    recentMatchOutcomesData && 'recentMatchOutcomes' in recentMatchOutcomesData

  const hasNonEmptyData =
    recentMatchOutcomesData &&
    (isCompetitorData
      ? recentMatchOutcomesData.recentMatchOutcomes.length > 0
      : recentMatchOutcomesData.homeTeam.recentMatchOutcomes.length > 0 &&
        recentMatchOutcomesData.awayTeam.recentMatchOutcomes.length > 0)

  const recentOutcomesContent = useMemo(() => {
    if (hasNonEmptyData) {
      return (
        <>
          {(isCompetitorData
            ? [recentMatchOutcomesData]
            : [
                recentMatchOutcomesData.homeTeam,
                recentMatchOutcomesData.awayTeam,
              ]
          ).map((team) => (
            <TeamOutcomes
              key={team.abbreviation}
              team={team}
              singleCompetitor={isCompetitorData}
            />
          ))}
        </>
      )
    } else {
      const placeholderMessage = error
        ? {
            iconName: 'user-robot-xmarks-solid' as SvgIconName,
            title: translate('network.error.title'),
            text: translate('network.error.text'),
          }
        : isFetched
          ? {
              iconName: 'calendar-clock' as SvgIconName,
              title: translate('sports.emptyData.title'),
              text: translate('sports.emptyData.text'),
            }
          : undefined

      return placeholderMessage ? (
        <Placeholder
          iconName={placeholderMessage.iconName}
          title={placeholderMessage.title}
          text={placeholderMessage.text}
        />
      ) : null
    }
  }, [
    error,
    hasNonEmptyData,
    isFetched,
    recentMatchOutcomesData,
    isCompetitorData,
  ])

  return (
    <SubsectionWrapper>
      <SubsectionTitle>{title}</SubsectionTitle>
      <StyledContentWrapper>{recentOutcomesContent}</StyledContentWrapper>
    </SubsectionWrapper>
  )
}

export default RecentMatchOutcomes
